import { useState, useEffect, useCallback } from "react";
import { Heart } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

// Lightweight in-memory cache shared across mounts so the heart state is
// consistent across pages without forcing every list item to re-fetch.
let favoritesCache = null;
const subscribers = new Set();

function notify() { subscribers.forEach(fn => fn()); }

async function fetchFavorites() {
  try {
    const { data } = await api.get("/favorites/ids");
    favoritesCache = new Set(data.ids || []);
    notify();
  } catch {
    favoritesCache = new Set();
    notify();
  }
}

export function useFavorites() {
  const { user } = useAuth();
  const [, force] = useState(0);

  useEffect(() => {
    if (!user) { favoritesCache = null; return; }
    if (favoritesCache === null) fetchFavorites();
    const fn = () => force(x => x + 1);
    subscribers.add(fn);
    return () => subscribers.delete(fn);
  }, [user]);

  const isFav = useCallback((spaceId) => favoritesCache?.has(spaceId) || false, []);
  const refresh = fetchFavorites;
  return { isFav, refresh };
}

export default function FavoriteButton({ spaceId, className = "", size = "md", onToggle }) {
  const { user } = useAuth();
  const { isFav } = useFavorites();
  const [busy, setBusy] = useState(false);
  const active = isFav(spaceId);

  const toggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast.error("Connectez-vous pour sauvegarder ce terrain en favori.");
      return;
    }
    if (busy) return;
    setBusy(true);
    try {
      if (active) {
        await api.delete(`/favorites/${spaceId}`);
        favoritesCache?.delete(spaceId);
        toast.success("Retiré des favoris");
      } else {
        await api.post(`/favorites/${spaceId}`);
        favoritesCache?.add(spaceId);
        toast.success("Ajouté à vos favoris");
      }
      notify();
      onToggle?.(!active);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const sz = size === "sm" ? "h-8 w-8" : "h-9 w-9";
  const iconSz = size === "sm" ? "h-4 w-4" : "h-[18px] w-[18px]";

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      aria-pressed={active}
      aria-label={active ? "Retirer des favoris" : "Ajouter aux favoris"}
      className={`${sz} flex items-center justify-center rounded-full bg-white/95 backdrop-blur-sm shadow-md hover:scale-110 transition-transform disabled:opacity-50 ${className}`}
      data-testid={`favorite-btn-${spaceId}`}
    >
      <Heart className={`${iconSz} transition-colors ${active ? "fill-red-500 text-red-500" : "text-zinc-700"}`} />
    </button>
  );
}
